import { Component, Optional } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from '@services/auth/auth.service';
import { AuthFormComponent } from '@shared/components/auth-form/auth-form.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
	selector: 'app-login',
	imports: [AuthFormComponent],
	templateUrl: './login.component.html',
	styleUrl: './login.component.scss',
})
export class LoginComponent {
	isRegister: 1 | 2 | 3 | 4 = 1; // 1 = Login, 4 = MFA
	errorMessage: string | null = null;
	isLoading = false;

	// MFA state
	mfaAccessToken: string | null = null;
	mfaFactorId: string | null = null;

	constructor(
		private authService: AuthService,
		private translocoService: TranslocoService,
		@Optional() public ref: DynamicDialogRef,
	) {}

	onLogin(formData: { email?: string; password?: string; mfaCode?: string; trustDevice?: boolean }): void {
		this.errorMessage = null;

		// Handle MFA verification
		if (this.isRegister === 4 && formData.mfaCode) {
			this.onVerifyMFA(formData.mfaCode, formData.trustDevice || false);
			return;
		}

		// Handle regular login
		if (!formData.email || !formData.password) {
			this.errorMessage = this.translocoService.translate('auth.errors.emailAndPasswordRequired');
			return;
		}

		const { email, password } = formData;

		this.isLoading = true;
		this.authService.login(email, password).subscribe({
			next: (response) => {
				// Check if MFA is required
				if (response.mfa_required) {
					this.isRegister = 4; // Switch to MFA mode
					this.mfaAccessToken = response.access_token;
					this.mfaFactorId = response.factor_id;
					this.isLoading = false;
				}
				// If no MFA, navigation is handled by auth service
			},
			error: (err) => {
				this.errorMessage = this.resolveAuthError(err);
				this.isLoading = false;
			},
		});
	}

	/**
	 * Maps the backend `{ code, message }` payload to a translated message.
	 * Backend strings stay in English (project convention: UI strings live
	 * in es.json, not in code). We translate by stable code; if no code
	 * matches, we fall back to the raw backend message so we don't swallow
	 * unexpected errors.
	 */
	private resolveAuthError(err: unknown): string {
		const code = (err as { error?: { code?: string } })?.error?.code;
		const backendMessage =
			(err as { error?: { message?: string }; message?: string })?.error?.message ??
			(err as { message?: string })?.message;

		const codeMap: Record<string, string> = {
			AUTH_USER_NOT_REGISTERED: 'auth.errors.userNotRegistered',
			AUTH_USER_NO_ROLE: 'auth.errors.userNoRole',
			AUTH_INVALID_CREDENTIALS: 'auth.errors.invalidCredentials',
			AUTH_EMAIL_NOT_VERIFIED: 'auth.errors.emailNotVerified',
			AUTH_INVALID_MFA_CODE: 'auth.login.mfa.invalidCode',
			AUTH_SESSION_FAILED: 'auth.errors.sessionFailed',
			AUTH_GENERIC: 'auth.errors.unknownError',
		};

		const i18nKey = code ? codeMap[code] : null;
		if (i18nKey) return this.translocoService.translate(i18nKey);
		return backendMessage || this.translocoService.translate('auth.errors.unknownError');
	}

	onVerifyMFA(mfaCode: string, trustDevice: boolean): void {
		if (!mfaCode || !this.mfaAccessToken || !this.mfaFactorId) {
			this.errorMessage = this.translocoService.translate('auth.login.mfa.invalidCode');
			return;
		}

		this.isLoading = true;
		this.authService.verifyMFALogin(this.mfaAccessToken, this.mfaFactorId, mfaCode, trustDevice).subscribe({
			next: () => {
				// Reset MFA state - navigation is handled by auth service
				this.isRegister = 1;
				this.mfaAccessToken = null;
				this.mfaFactorId = null;
				this.errorMessage = null;
			},
			error: () => {
				this.errorMessage = this.translocoService.translate('auth.login.mfa.invalidCode');
				this.isLoading = false;
			},
		});
	}

	cancelMFA(): void {
		this.isRegister = 1;
		this.mfaAccessToken = null;
		this.mfaFactorId = null;
		this.errorMessage = null;
	}
}
